import { Language } from '@/shared/lib/i18n';
import { OutreachEditorTokenKey } from '@recruit-robin/robin-theme/web-components';
import { CandidateDto } from './candidate.dto';
import {
  Education,
  Experience,
  Insights,
  Location,
  Preferences,
  toEducations,
  toExperiences,
  toInsights,
  toLocation,
  toPreferences,
  toVerified,
  toVolunteering,
  Verified,
  Volunteering,
} from './projection';

type TotalTime = {
  first: number | undefined;
  last: number | undefined;
  gap: number | undefined;
};

const stripPlatformPrefix = (esPersonId: string): string => {
  const dashIndex = esPersonId.indexOf('-');
  return dashIndex === -1 ? esPersonId : esPersonId.slice(dashIndex + 1);
};

export type CandidateViewModel = {
  esPersonId: string;
  esPersonIdWithoutPlatform: string;
  score: number;
  name: string;
  firstName: string;
  lastName: string;
  isAnonymous: boolean;
  location: Location;
  experiences: Experience[];
  educations: Education[];
  volunteering: Volunteering[];
  preferences: Preferences;
  verified: Verified;
  lastRefresh: string | undefined;
  insights: Insights;
  totalTimeExperiences: TotalTime;
  totalTimeVolunteeringExperiences: TotalTime;
  totalTimeEducations: TotalTime;
  matchReason: Record<Language, string>;
  /**
   * @deprecated
   * This should be avoided as much as possible. Unfortunatelly we need this to integrate with the legacy code.
   */
  dto: CandidateDto;
};

export const toCandidateViewModel = (dto: CandidateDto): CandidateViewModel => {
  // dto._source.name is empty when the candidate chose to stay anonymous on their source
  // platform (e.g. werk.nl). We still fall back to headline/job title for fullName/firstName
  // below, but only so the candidate list/card has something to display (see DEVP-2567) -
  // isAnonymous is the explicit signal for anything that would otherwise treat that fallback
  // as if it were the candidate's real name (e.g. message personalization).
  const isAnonymous = !dto._source.name;
  const fullName = dto._source.name ?? dto._source.headline ?? '';
  const nameParts = fullName.trim().split(' ');
  const firstName = nameParts[0] ?? '';
  const lastName = nameParts.slice(1).join(' ');

  return {
    esPersonId: dto._id,
    esPersonIdWithoutPlatform: stripPlatformPrefix(dto._id),
    score: dto._score,
    name: fullName,
    firstName,
    lastName,
    isAnonymous,
    location: toLocation(dto),
    educations: toEducations(dto),
    matchReason: {
      en: dto.summary_reason_en ?? '',
      nl: dto.summary_reason_nl ?? '',
    },
    totalTimeEducations: {
      first: dto._source.meta?.experience?.academic?.first ?? undefined,
      last: dto._source.meta?.experience?.academic?.last ?? undefined,
      gap: dto._source.meta?.experience?.academic?.gap ?? undefined,
    },
    experiences: toExperiences(dto),
    totalTimeExperiences: {
      first: dto._source.meta?.experience?.professional?.first ?? undefined,
      last: dto._source.meta?.experience?.professional?.last ?? undefined,
      gap: dto._source.meta?.experience?.professional?.gap ?? undefined,
    },
    preferences: toPreferences(dto),
    verified: toVerified(dto),
    lastRefresh: dto._source.last_insight_date,
    insights: toInsights(dto),
    volunteering: toVolunteering(dto),
    totalTimeVolunteeringExperiences: {
      first: dto._source.meta?.experience?.volunteer?.first ?? undefined,
      last: dto._source.meta?.experience?.volunteer?.last ?? undefined,
      gap: dto._source.meta?.experience?.volunteer?.gap ?? undefined,
    },

    dto: dto,
  };
};

export const toOutreachEditorTokens = (candidate: CandidateViewModel): Record<OutreachEditorTokenKey, string> => {
  return {
    '{firstName}': candidate.firstName,
    '{lastName}': candidate.lastName,
    '{fullName}': candidate.name,
    '{location}': candidate.location?.name ?? '',
    '{lastPosition}': candidate.experiences[0]?.role ?? '',
    '{lastCompany}': candidate.experiences[0]?.companyName ?? '',
    '{recruiterFullName}': '',
    '{nameJobboard}': '',
    '{nameVacancy}': '',
    '{schedulerLink}': '',
  };
};
